/**
 * GivingCare Lumen — motion presets.
 *
 * House rules baked in here:
 *  - Only `opacity` and `transform` are ever animated (compositor-only, so a
 *    slow page never turns a transition into a layout thrash).
 *  - Every preset has a reduced-motion twin that keeps the state change legible
 *    but removes the travel.
 *
 * Consumers should spread a preset onto a `motion.*` element:
 *
 *     import { motion } from "motion/react";
 *     import { fadeInUp } from "../ui/motionPresets";
 *     <motion.div {...fadeInUp}>…</motion.div>
 *
 * or call `reduced(fadeInUp)` / the `use*` helpers when they need the variant
 * chosen at render time.
 */

/* -------------------------------------------------------------------------
   Primitives
   ------------------------------------------------------------------------- */

export const durations = {
  fast: 0.16,
  base: 0.24,
  slow: 0.38,
};

export const easing = {
  /** Entrances and most state changes. */
  standard: [0.2, 0, 0, 1],
  /** Exits — accelerate away. */
  exit: [0.4, 0, 1, 1],
};

export const spring = {
  type: "spring",
  stiffness: 380,
  damping: 32,
};

/** How long a reduced-motion transition lasts: effectively instant. */
const REDUCED_DURATION = 0.01;

/** Max children a stagger will walk before it stops adding delay. */
export const STAGGER_CAP = 12;

/* -------------------------------------------------------------------------
   Reduced-motion detection
   ------------------------------------------------------------------------- */

/**
 * True when the user has asked for reduced motion.
 * Safe in jsdom/SSR where `matchMedia` may be missing.
 */
export function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Strip travel out of a preset, keeping only opacity changes.
 * Used to build every `*Reduced` export below, and exported so downstream
 * code can reduce its own one-off presets consistently.
 */
export function toReduced(preset) {
  const keepOpacity = (state) => {
    if (!state || typeof state !== "object") return state;
    return "opacity" in state ? { opacity: state.opacity } : {};
  };

  const out = {
    transition: { duration: REDUCED_DURATION },
  };
  if (preset.initial !== undefined) out.initial = keepOpacity(preset.initial);
  if (preset.animate !== undefined) out.animate = keepOpacity(preset.animate);
  if (preset.exit !== undefined) out.exit = keepOpacity(preset.exit);
  if (preset.whileTap !== undefined) out.whileTap = {};
  if (preset.whileHover !== undefined) out.whileHover = {};
  if (preset.variants !== undefined) {
    out.variants = Object.fromEntries(
      Object.entries(preset.variants).map(([key, value]) => {
        const transition = { duration: REDUCED_DURATION };
        // A stagger is motion too: collapse it so a reduced-motion list
        // appears at once instead of marching in. Without this, reduced()
        // would disagree with the hand-written listStaggerReduced export.
        if (value && typeof value === "object" && value.transition) {
          if ("staggerChildren" in value.transition) transition.staggerChildren = 0;
          if ("delayChildren" in value.transition) transition.delayChildren = 0;
        }
        return [key, { ...keepOpacity(value), transition }];
      })
    );
  }
  return out;
}

/**
 * Pick the right variant of a preset for the current user.
 *
 *   <motion.div {...reduced(fadeInUp)} />
 *
 * Called with no argument it reports the raw preference, so it doubles as the
 * boolean check the spec asks for.
 */
export function reduced(preset) {
  const isReduced = prefersReducedMotion();
  if (preset === undefined) return isReduced;
  return isReduced ? toReduced(preset) : preset;
}

/* -------------------------------------------------------------------------
   Presets
   ------------------------------------------------------------------------- */

/** Standard entrance: rise 12px while fading in. */
export const fadeInUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: durations.base, ease: easing.standard },
};

export const fadeInUpReduced = toReduced(fadeInUp);

/** Modals, popovers, toasts — springs in, eases out. */
export const scaleIn = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.97 },
  transition: spring,
};

export const scaleInReduced = toReduced(scaleIn);

/** Page-level entrance — shallower travel than a card. */
export const pagePreset = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: durations.base, ease: easing.standard },
};

export const pagePresetReduced = toReduced(pagePreset);

/** Tap/hover affordance for buttons and cards. */
export const pressable = {
  whileTap: { scale: 0.97 },
  whileHover: { y: -1 },
};

export const pressableReduced = toReduced(pressable);

/* ---- List stagger ------------------------------------------------------ */

/**
 * Parent of a staggered list. Children must use `listStaggerChild`.
 *
 *   <motion.ul {...reduced(listStagger)}>
 *     {items.map(i => <motion.li key={i.id} variants={listStaggerChild.variants} />)}
 *   </motion.ul>
 */
export const listStagger = {
  initial: "hidden",
  animate: "visible",
  variants: {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.035,
        /* Past the cap the delay stops growing, so a 500-row table does not
           take 17 seconds to finish appearing. */
        delayChildren: 0,
      },
    },
  },
};

export const listStaggerReduced = {
  initial: "hidden",
  animate: "visible",
  variants: {
    hidden: {},
    visible: { transition: { staggerChildren: 0, duration: REDUCED_DURATION } },
  },
};

/** Child of `listStagger` — the fadeInUp motion expressed as variants. */
export const listStaggerChild = {
  variants: {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: durations.base, ease: easing.standard },
    },
  },
};

export const listStaggerChildReduced = {
  variants: {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: REDUCED_DURATION } },
  },
};

/**
 * Cap the stagger: items past `STAGGER_CAP` animate with no extra delay.
 * Apply to the item's `custom` or use the returned props directly.
 */
export function staggerDelay(index) {
  return Math.min(index, STAGGER_CAP) * 0.035;
}

/* -------------------------------------------------------------------------
   Convenience: preset bundle already resolved for the current preference
   ------------------------------------------------------------------------- */

/** Every preset, resolved for the current reduced-motion preference. */
export function resolvedPresets() {
  const isReduced = prefersReducedMotion();
  return {
    fadeInUp: isReduced ? fadeInUpReduced : fadeInUp,
    scaleIn: isReduced ? scaleInReduced : scaleIn,
    pagePreset: isReduced ? pagePresetReduced : pagePreset,
    pressable: isReduced ? pressableReduced : pressable,
    listStagger: isReduced ? listStaggerReduced : listStagger,
    listStaggerChild: isReduced ? listStaggerChildReduced : listStaggerChild,
  };
}
